import { BaseLocationType } from '../models/BaseLocation';
import { MediaTypeEnum } from '../gql/schema';
import type { BaseInstanceConfig } from '../models/BaseInstanceConfig';
import { BaseTourDifficulty } from '../models/BaseTourDifficulty';
import { WhlConsentManagerType } from '../models/WhlConsentManagerType';
import { ConsentVendor } from '@utils/consentManager/consent-manager';
import { mediaLicenseIconMapping } from '@utils/mediaLicense';
import { Module } from '@models/Module';

export enum NrwtMediaType {
  STANDARD = 1,
  VORSCHAUBILD = 2,
  HERO_BILD = 3,
  VIDEO_VORSCHAU = 4,
  IMPORTBILD = 5,
  DEFAULT_BILD_DATAHUB = 6,
  GALLERY_BILD_DATAHUB = 7,
}

enum NrwtEventCriterium {
  SCHLECHTWETTER_AKTIVITÄT = -2,
  EISBAHN = 1,
  FACHWERKKULISSE = 2,
  MITTELALTERLICH = 3,
  SCHLÖSSER_UND_BURGEN = 4,
  AUßERGEWÖHNLICHE_KULISSE = 5,
  STÄDTE = 6,
  MUSIK_WISSENSCHAFT_GESELLSCHAFT = 7,
  KUNST_DESIGN_ARCHITEKTUR = 8,
  EMFIEBER_FUßBALLKUNST_FANKULTUR = 9,
  MANUELL_KURATIERT = 10,
  TEST_EVENT_KATEGORIE_FÜR_MAX_UND_JENS = 11,
  LAMA_EVENT = 12,
  EVENTTERMINE_KURATIERT = 13,
}

enum NrwtTourCategory {
  AKTIV_UND_OUTDOOR = 14,
  RADFAHREN = 2,
  WANDERN = 1,

  // Aktiv & Outdoor
  BARRIEREFREI = 15,
  BOOTS_UND_KANUTOUR = 16,
  INLINERTOUR = 17,
  JOGGING = 18,
  MOTORISIERTE_TOUR = 19,
  PFERDETOUR = 20,
  THEMENSTRAßE = 21,
  WINTERTOUR = 22,

  // Radfahren
  FERNRADWEG = 11,
  FLUSSRADWEG = 12,
  GRAVELBIKE = 4,
  MOUNTAINBIKE = 5,
  REGIONALE_RADTOUR = 13,
  RENNRAD = 3,

  // Wandern
  FERNWANDERWEG = 6,
  KLETTERSTEIG = 7,
  REGIONALER_WANDERWEG = 8,
  STADTRUNDGANG = 9,
  THEMENWEG = 10,
}

export enum NrwtProductline {
  DATAHUB_NRW = 2,
  KATEGORIEN_WIDGETS = 202,
  KULTUR_UND_GESCHICHTE = 211,
  KULTUR_ENTDECKEN = 214,
  REISE_IN_DIE_VERGANGENHEIT = 205,
  KI_TEXTE = 201,
  POI = 25,
  AUSFLUGSZIELE = 26,
  MUSEUM_AUSSTELLUNG = 28,
  ORT = 50,
  WENNS_REGNET = 210,
}

export enum NrwtModule {
  REISEN_FUER_ALLE = 100016000,
}

export enum ReisenFuerAlleCriteria {
  GEHBEHINDERUNG = 1,
  GEHBEHINDERUNG_TEILWEISE = 2,
  HOERBEHINDERUNG = 3,
  HOERBEHINDERUNG_TEILWEISE = 4,
  SEHBEHINDERUNG = 5,
  SEHBEHINDERUNG_TEILWEISE = 6,
  ROLLSTUHLFAHRER = 7,
  ROLLSTUHLFAHRER_TEILWEISE = 8,
  BLIND = 9,
  BLIND_TEILWEISE = 10,
  KOGNITIVEN_BEEINTRAECHTIGUNGEN = 11,
  GEHOERLOS = 14,
  GEHOERLOS_TEILWEISE = 15,
}

export const CONFIG: Omit<BaseInstanceConfig, 'instanceId'> = {
  activatedModules: [{ module: Module.RFA, plId: NrwtModule.REISEN_FUER_ALLE }],
  bookmarks: {
    itinerary: {
      boundingBox: {
        northeast: {
          latitude: 52.531,
          longitude: 9.462,
        },
        southwest: {
          latitude: 50.323,
          longitude: 5.866,
        },
      },
    },
  },
  defaultWidgetId: 8,
  defaults: {
    hero: {
      filter: {
        mediaType: [MediaTypeEnum.Image],
        mediaProfile: { eq: NrwtMediaType.DEFAULT_BILD_DATAHUB },
      },
      mediaLicenseIconMapping: mediaLicenseIconMapping,
      descriptionHints: {
        showNoWarranty: false,
        showMediaLicense: false,
      },
    },
    gallery: {
      filter: {
        or: [
          {
            mediaType: [MediaTypeEnum.Image],
            mediaProfile: {
              oneOf: [NrwtMediaType.GALLERY_BILD_DATAHUB],
            },
          },
          { mediaType: [MediaTypeEnum.Video] },
        ],
      },
    },
    list: {
      filter: {
        mediaType: [MediaTypeEnum.Image],
        mediaProfile: { oneOf: [NrwtMediaType.DEFAULT_BILD_DATAHUB] },
      },
    },
    article: {
      contact: {
        type: 'contact',
      },
    },
  },
  event: {
    detail: {
      useWeatherModule: {
        linkQueryParams: {
          criteria: [NrwtEventCriterium.SCHLECHTWETTER_AKTIVITÄT],
        },
      },
      hero: {
        showCategoryInfoTags: false,
      },
    },
    overview: {
      teaser: {
        box: {
          showCategory: false,
        },
      },
    },
    list: {
      teaser: {
        list: {
          showCategory: false,
        },
      },
    },
  },
  footerAdditionalLogos: [
    '/assets/images/instances/nrwt/Kofinanziert von der EU_RGB_POSITIV.png',
    '/assets/images/instances/nrwt/AK_Wirtschaft, Industrie, Klimaschutz und Energie_Farbig_RGB.png',
  ],
  footerContact: {
    name: 'Tourismus NRW e.V.',
    street: 'Völklinker Straße',
    streetNo: '4',
    zipcode: '40219',
    city: 'Düsseldorf',
    email: 'info@nrw-tourismus.de',
  },
  global: {
    features: {
      ai: true,
    },
    geoBoundingBox: [5.8664, 50.3276, 9.4623, 52.5325],
  },
  graphqlBearerToken:
    'eyJraWQiOiJpbXgtY2RhIiwidHlwIjoiSldUIiwiYWxnIjoiUlMyNTYifQ.eyJpc3MiOiJodHRwczpcL1wvbnJ3LmlteHBsYXRmb3JtLmRlXC9vYXV0aCIsInN1YiI6IjMzIiwibG5hbWUiOiJ3cy53aGl0ZWxhYmVsLXdpZGdldHMifQ.rXpW8NHiI7OnU-FnwP2xhjMnMqfFqmFo3JBP6VpJhJ9YdvlLUcQkWd2gn8bwnP_CAP4sHtI2fEnElmlelRHipFKoTPoRycRzznhcttvbLzaDme1LBqygPkU0sTmrwcWv9Sa4-1C6mwtJ3GEBtORnN_O5rUPHgmh-AIg25g0hZZmJ9F4clnVA_vukTZkI_vzZR-zXrsW3bDLzXKGNmjj7ZMOUcEb_4x7_L3C15rrnCdqDcVbqX_xKOS4iHYPwunG4AWHWij5mEmdoP-zW7gHuSSJjH4YD3H45IUogqNGQP934Rn8P0tLKSkJE-SbU_SIWZe_v1e-hQMi-UnrL-ix6ig',
  graphqlEndpoint: `https://content-delivery.imxplatform.de/nrwt/imxplatform`,
  itinerary: {
    itinerary: {
      showCategory: false,
      poi: {
        productlinesRootId: 25,
        categoryBlacklist: [50],
      },
    },
  },
  map: {
    defaultCoords: {
      // nrw
      latitude: 51.493155254942224,
      longitude: 7.555403951753447,
    },
    maptoolkitApiKey: 'nrw',
  },
  pageheader: {
    search: {
      locationSearchWhitelist: [BaseLocationType.ADDRESS_POI_GROUP, BaseLocationType.LOCATION, BaseLocationType.REGION],
    },
  },
  poi: {
    detail: {
      useWeatherModule: {
        linkQueryParams: {
          productlines: [NrwtProductline.WENNS_REGNET],
        },
      },
      hero: {
        showCategoryInfoTags: false,
        certificates: {
          mainImage: '/assets/images/instances/nrwt/accessibility/logo.png',
          certificatesPictoMapping: {
            [ReisenFuerAlleCriteria.GEHBEHINDERUNG]: `/assets/images/instances/nrwt/accessibility/${ReisenFuerAlleCriteria.GEHBEHINDERUNG}.png`,
            [ReisenFuerAlleCriteria.GEHBEHINDERUNG_TEILWEISE]: `/assets/images/instances/nrwt/accessibility/${ReisenFuerAlleCriteria.GEHBEHINDERUNG}.png`,
            [ReisenFuerAlleCriteria.HOERBEHINDERUNG]: `/assets/images/instances/nrwt/accessibility/${ReisenFuerAlleCriteria.HOERBEHINDERUNG}.png`,
            [ReisenFuerAlleCriteria.HOERBEHINDERUNG_TEILWEISE]: `/assets/images/instances/nrwt/accessibility/${ReisenFuerAlleCriteria.HOERBEHINDERUNG}.png`,
            [ReisenFuerAlleCriteria.SEHBEHINDERUNG]: `/assets/images/instances/nrwt/accessibility/${ReisenFuerAlleCriteria.SEHBEHINDERUNG}.png`,
            [ReisenFuerAlleCriteria.SEHBEHINDERUNG_TEILWEISE]: `/assets/images/instances/nrwt/accessibility/${ReisenFuerAlleCriteria.SEHBEHINDERUNG}.png`,
            [ReisenFuerAlleCriteria.ROLLSTUHLFAHRER]: `/assets/images/instances/nrwt/accessibility/${ReisenFuerAlleCriteria.ROLLSTUHLFAHRER}.png`,
            [ReisenFuerAlleCriteria.ROLLSTUHLFAHRER_TEILWEISE]: `/assets/images/instances/nrwt/accessibility/${ReisenFuerAlleCriteria.ROLLSTUHLFAHRER}.png`,
            [ReisenFuerAlleCriteria.BLIND]: `/assets/images/instances/nrwt/accessibility/${ReisenFuerAlleCriteria.BLIND}.png`,
            [ReisenFuerAlleCriteria.BLIND_TEILWEISE]: `/assets/images/instances/nrwt/accessibility/${ReisenFuerAlleCriteria.BLIND}.png`,
            [ReisenFuerAlleCriteria.KOGNITIVEN_BEEINTRAECHTIGUNGEN]: `/assets/images/instances/nrwt/accessibility/${ReisenFuerAlleCriteria.KOGNITIVEN_BEEINTRAECHTIGUNGEN}.png`,
            [ReisenFuerAlleCriteria.GEHOERLOS]: `/assets/images/instances/nrwt/accessibility/${ReisenFuerAlleCriteria.GEHOERLOS}.png`,
            [ReisenFuerAlleCriteria.GEHOERLOS_TEILWEISE]: `/assets/images/instances/nrwt/accessibility/${ReisenFuerAlleCriteria.GEHOERLOS}.png`,
          },
        },
      },
    },
    productlineInfoTagRootId: NrwtProductline.POI,
    productlineInfoTagBlacklist: [NrwtProductline.POI],
    productlineFilterGroupBlacklist: {
      ids: [NrwtProductline.POI],
    },
    list: {
      teaser: {
        list: {
          showCategory: false,
        },
      },
    },
    overview: {
      teaser: {
        box: {
          showCategory: false,
        },
      },
    },
  },
  portalConsentManager: {
    type: WhlConsentManagerType.CONSENT_MANAGER,
    config: {
      consentManagerId: 'a7234e5ba1e7f',
      vendorIds: {
        [ConsentVendor.MAPTOOLKIT]: 'c54313',
        [ConsentVendor.YOUTUBE]: 's30',
        [ConsentVendor.GTAG]: 's26',
      },
    } satisfies ConsentManagerConfig,
  },
  robots: 'noindex, nofollow',
  scripts: [
    {
      src: 'https://www.googletagmanager.com/gtag/js?id=G-HTTVQ75FBH',
      vendorInfo: 's26',
      async: true,
      class: 'cmplazyload',
    },
    {
      innerHTML: `
              window.dataLayer = window.dataLayer || [];  function gtag(){dataLayer.push(arguments);}  gtag('js', new Date());  gtag('config', 'G-HTTVQ75FBH');
      `,
      vendorInfo: 's26',
      class: 'cmplazyload',
    },
  ],
  tour: {
    detail: {
      useWeatherModule: true,
    },
    categoryIcons: {
      [NrwtTourCategory.AKTIV_UND_OUTDOOR]: 'ion:active',
      [NrwtTourCategory.MOTORISIERTE_TOUR]: 'ph:motorcycle-fill',
      [NrwtTourCategory.WINTERTOUR]: 'lets-icons:winter',
      [NrwtTourCategory.RADFAHREN]: 'ion:bicycle',
      [NrwtTourCategory.STADTRUNDGANG]: 'icon-park-solid:turn-around',
      [NrwtTourCategory.WANDERN]: 'material-symbols:hiking',
      [NrwtTourCategory.BOOTS_UND_KANUTOUR]: 'ion:boat-outline',
    },
    difficultyMapping: {
      1: BaseTourDifficulty.EASY,
      2: BaseTourDifficulty.EASY,
      3: BaseTourDifficulty.MEDIUM,
      4: BaseTourDifficulty.HARD,
      5: BaseTourDifficulty.HARD,
    },
  },
};
